import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import MIHeader from '../MIHeader';
import { slugify } from '../utils';
import { Helmet } from 'react-helmet';
import { useTranslation } from '../../../hooks/useTranslation';
import { localizedFactoryName, localizedStateName, localizedDistrictName } from '../../../utils/localizedDisplayName';

const SearchPage = () => {
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const runSearch = async (q) => {
    if (!q || q.trim().length < 2) { setResults(null); return; }
    setLoading(true);
    try {
      const { data: millMatches } = await supabase
        .from('mi_mills')
        .select('mill_code, canonical_name, source_state')
        .ilike('canonical_name', `%${q}%`)
        .limit(8);

      const { data: allDistricts } = await supabase
        .from('mi_canonical_districts')
        .select('name');
      const { data: allStates } = await supabase
        .from('mi_canonical_states')
        .select('name');

      const districtMatches = (allDistricts || []).filter((d) => d.name.toLowerCase().includes(q.toLowerCase())).slice(0, 5);
      const stateMatches = (allStates || []).filter((s) => s.name.toLowerCase().includes(q.toLowerCase())).slice(0, 5);

      setResults({ mills: millMatches || [], districts: districtMatches, states: stateMatches });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    runSearch(v);
  };

  const totalResults = results ? results.mills.length + results.districts.length + results.states.length : 0;

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      <Helmet>
        <title>Search Sugar Mills & Districts | SugarHathi Market Intelligence</title>
        <meta name="description" content="Search live sugar quota data across 621+ mills, all states and districts in India. By SugarHathi." />
      </Helmet>
      <MIHeader subtitle={t('common.search', 'Search')} />
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-2xl py-8">
        <h1 className="text-2xl font-black text-[var(--text-primary)] mb-4">{t('common.search', 'Search')}</h1>
        <div className="flex items-center gap-2 bg-[var(--bg-card)] border border-[var(--border-primary)] rounded-[var(--radius-input)] px-3 py-2.5 mb-6">
          <Icon name="Search" size={18} className="text-[var(--text-muted)]" />
          <input
            autoFocus
            value={query}
            onChange={handleChange}
            placeholder="Search factory, district, or state"
            className="flex-1 bg-transparent text-[var(--text-primary)] text-sm outline-none placeholder:text-[var(--text-muted)]"
          />
        </div>

        {loading && <p className="text-sm text-[var(--text-muted)]">Searching...</p>}

        {!loading && results && totalResults === 0 && (
          <p className="text-sm text-[var(--text-muted)]">No results for "{query}"</p>
        )}

        {!loading && results && results.states.length > 0 && (
          <div className="mb-5">
            <p className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wide mb-2">{t('marketplace.allStates', 'States')}</p>
            {results.states.map((s, i) => (
              <button key={i} onClick={() => navigate(`/market-intelligence/state/${slugify(s.name)}`)} className="w-full flex items-center gap-2 py-2 text-left hover:bg-[var(--bg-surface)] rounded-[var(--radius-input)] px-2">
                <Icon name="Map" size={15} style={{ color: 'var(--info)' }} className="flex-shrink-0" />
                <span className="text-sm text-[var(--text-primary)]">{localizedStateName(s.name, language)}</span>
              </button>
            ))}
          </div>
        )}

        {!loading && results && results.districts.length > 0 && (
          <div className="mb-5">
            <p className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wide mb-2">{t('marketIntelligence.districtsNav')}</p>
            {results.districts.map((d, i) => (
              <button key={i} onClick={() => navigate(`/market-intelligence/district/${slugify(d.name)}`)} className="w-full flex items-center gap-2 py-2 text-left hover:bg-[var(--bg-surface)] rounded-[var(--radius-input)] px-2">
                <Icon name="MapPin" size={15} style={{ color: 'var(--success)' }} className="flex-shrink-0" />
                <span className="text-sm text-[var(--text-primary)]">{localizedDistrictName(d.name, undefined, language)}</span>
              </button>
            ))}
          </div>
        )}

        {!loading && results && results.mills.length > 0 && (
          <div>
            <p className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wide mb-2">{t('marketIntelligence.allFactories')}</p>
            {results.mills.map((m, i) => (
              <button key={i} onClick={() => navigate(`/market-intelligence/factory/${slugify(m.canonical_name)}`)} className="w-full flex items-center gap-2 py-2.5 text-left hover:bg-[var(--bg-surface)] rounded-[var(--radius-input)] px-2">
                <Icon name="Factory" size={15} style={{ color: 'var(--primary)' }} className="flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm text-[var(--text-primary)] truncate">{localizedFactoryName(m, language)}</p>
                  <p className="text-xs text-[var(--text-muted)]">{localizedStateName(m.source_state, language)} &middot; Code {m.mill_code}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default SearchPage;
